import thousandCommas from 'components/Dashboard/helpers/thousandCommas';
import useOutsideElement from 'components/Dashboard/utilities/useOutsideElement';
import React, { useRef, useState } from 'react';
import {
  monitoringData,
  supportData,
  influenceData,
  researchData,
  reportsData,
  embeddablesData,
  analyticsData,
  briefPolicymakersData,
} from './PricingData';
import { useAuth0 } from '@auth0/auth0-react';
import PaymentButton, { EnterprisePaymentButton } from 'components/Pricing/PaymentButton';

const PricingPageMatrix = ({ activeTab, highlightedChannel, paymentButtonProps, planName }) => {
  const { conditionForUpgrade } = paymentButtonProps || {};
  const { isAuthenticated } = useAuth0();
  const showFreePlan = import.meta.env.VITE_APP_SHOW_FREE_PLAN.toLowerCase() !== 'false';

  const contentContainer = useRef(null);
  const titleSection = useRef(null);

  return (
    <section id='features'>
      <div
        className={`pricing-features p-lg-5 py-5 px-3 ${!showFreePlan ? 'pricing-without-free' : ''} ${
          isAuthenticated ? 'pricing-features-authenticated' : ''
        }`}
      >
        <h1 className='text-center mt-2 mb-5'>Compare plans</h1>
        <div className='container'>
          <div className={`table-responsive pricing-table`} ref={contentContainer}>
            <table className='table'>
              <thead ref={titleSection}>
                <tr>
                  <th scope='col' className='pricing-plan-title'></th>
                  <>
                    {activeTab === 'companies' && (
                      <>
                        {showFreePlan && (
                          <th scope='col' className='pricing-plan-title'>
                            <p>Free</p>
                            <PaymentButton plan={'Free'} compare {...paymentButtonProps} />
                          </th>
                        )}
                        {!showFreePlan && (
                          <th scope='col' className='pricing-plan-title'>
                            <p>Starter</p>
                            <PaymentButton plan={'Starter'} compare {...paymentButtonProps} />
                          </th>
                        )}

                        <th scope='col' className='pricing-plan-title'>
                          <p>Pro</p>
                          <div className={`${highlightedChannel === 'Pro' ? 'highlighted-payment-button' : ''}`}>
                            <PaymentButton plan={'Pro'} compare {...paymentButtonProps} />
                          </div>
                        </th>
                      </>
                    )}
                  </>
                  <th scope='col' className='pricing-plan-title'>
                    <p>Mogul</p>
                    <div className={`${highlightedChannel === 'Mogul' ? 'highlighted-payment-button' : ''}`}>
                      <PaymentButton plan={'Mogul'} compare {...paymentButtonProps} />
                    </div>
                  </th>
                  {highlightedChannel === 'Mogul' && (
                    <th scope='col' className='pricing-plan-title'>
                      <p>Enterprise</p>
                      <EnterprisePaymentButton planName={planName} conditionForUpgrade={conditionForUpgrade} />
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                <tr className='pricing-matrix-title pricing-users-title'>
                  <td>
                    <h4>Users</h4>
                  </td>
                  {activeTab === 'companies' && (
                    <>
                      <td>1</td>
                      <td>unlimited</td>
                    </>
                  )}
                  <td>unlimited</td>
                  {highlightedChannel === 'Mogul' && <td>unlimited</td>}
                </tr>
                <CreateDataComponent data={monitoringData} name={'Monitor'} activeTab={activeTab} />
                <CreateDataComponent data={influenceData} name={'People'} activeTab={activeTab} />
                <CreateDataComponent data={analyticsData} name={'Analytics'} activeTab={activeTab} />
                <CreateDataComponent data={briefPolicymakersData} name={'Brief policymakers'} activeTab={activeTab} />
                {activeTab !== 'agencies' && (
                  <CreateDataComponent data={reportsData} name={'Reports'} activeTab={activeTab} />
                )}
                <CreateDataComponent data={researchData} name={'Research'} activeTab={activeTab} />
                <CreateDataComponent data={embeddablesData} name={'Embeddables'} activeTab={activeTab} />
                <CreateDataComponent data={supportData} name={'Support'} activeTab={activeTab} />
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>
  );
};

const CreateDataComponent = (props) => {
  const { data, name, activeTab } = props;
  const [customText, setCustomText] = useState({
    basic: 'included',
    pro: 'included',
    mogul: 'included',
    custom: 'unlimited',
  });
  const highlightedChannel = import.meta.env.VITE_APP_HIGHLIGHTED_CHANNEL;
  const showFreePlan = import.meta.env.VITE_APP_SHOW_FREE_PLAN.toLowerCase() !== 'false';

  const tickElement = (feature, name) => {
    return (
      <td>
        {feature && feature !== 'no include' ? (
          feature === 'yes' ? (
            <img className='circle-tick' src={`${import.meta.env.VITE_APP_CDNURL}/images/circle-tick.svg`} alt='True' />
          ) : (
            <>{typeof feature === 'string' ? feature : feature()}</>
          )
        ) : (
          '—'
        )}
      </td>
    );
  };

  const hoverTextCondition = (item) => {
    if (activeTab === 'agencies') {
      return item.agenciesHoverText ? item.agenciesHoverText !== 'hide' : item.hoverText;
    } else {
      return item.hoverText;
    }
  };

  return (
    <>
      <tr className='pricing-matrix-title'>
        <td>
          <h4>{name}</h4>
        </td>
        {activeTab === 'companies' && (
          <>
            <td />
            <td />
          </>
        )}
        <td />
        {highlightedChannel === 'Mogul' && <td />}
      </tr>
      {data.map((item, index) => {
        const {
          hideForAgencies,
          name,
          agenciesName,
          agenciesHoverText,
          hoverText,
          free,
          basic,
          pro,
          mogul,
          custom,
          mogulAgencies,
          customAgencies,
          customDropdown,
          tooltipClass,
        } = item;
        return (
          <React.Fragment key={`feature${index}`}>
            {(activeTab !== 'agencies' || (activeTab === 'agencies' && !hideForAgencies)) && (
              <tr className={`${name}-feature-${index}`}>
                <td>
                  <p className={`mb-0 flex-centered pricing-feature-item`}>
                    <span>{activeTab === 'agencies' && agenciesName ? agenciesName : name}</span>
                    {item.beta && (
                      <span className='text-uppercase main-light-text paragraph-p7 ml-2 beta-feature bg-white'>
                        Beta
                      </span>
                    )}
                    {hoverTextCondition(item) && (
                      <span
                        className='pricing-feature-more'
                        data-tooltip-html={
                          activeTab === 'agencies' && agenciesHoverText ? agenciesHoverText : hoverText
                        }
                        data-tooltip-class-name={tooltipClass ?? 'header-tooltip pricing-feature-tooltip'}
                        data-tooltip-id={'pricing-page-tooltip'}
                      >
                        More information
                      </span>
                    )}
                    <span>{customDropdown && <DropdownContacts setCustomText={setCustomText} />}</span>
                  </p>
                </td>
                {activeTab === 'companies' && (
                  <>
                    {customDropdown ? <td>{customText?.pro}</td> : tickElement(showFreePlan ? free : basic)}
                    {customDropdown ? <td>{customText?.pro}</td> : <>{tickElement(pro)}</>}
                  </>
                )}
                {customDropdown ? (
                  <td>{customText?.mogul}</td>
                ) : (
                  <>{tickElement(mogulAgencies && activeTab === 'agencies' ? mogulAgencies : mogul)}</>
                )}
                {highlightedChannel === 'Mogul' && (
                  <>
                    {customDropdown ? (
                      <td>{customText?.custom}</td>
                    ) : (
                      <>{tickElement(customAgencies && activeTab === 'agencies' ? customAgencies : custom)}</>
                    )}
                  </>
                )}
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
};

const DropdownContacts = (props) => {
  const { setCustomText } = props;
  const [value, setValue] = useState(50);
  const [isOpen, setIsOpen] = useState(false);

  const boxElement = useRef(null);
  const hideBox = () => {
    setIsOpen(false);
  };
  useOutsideElement(boxElement, hideBox);

  return (
    <span className='d-block position-relative select-contact-pricing ml-3' ref={boxElement}>
      <span
        className={`border box-shadow pointer pl-1 pr-3 py-1 rounded dropdown-item-element select-contact-value action-state-hover ${
          isOpen ? 'open' : ''
        }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{thousandCommas(value)}</span>
      </span>
      {isOpen && (
        <div className='frequency-dropdown scroll-container search-bar-lists'>
          {Array(40)
            .fill()
            .map((item, index) => {
              let itemValue = (index + 1) * 50;
              return (
                <p
                  key={`dropdown-contact-${index}`}
                  className={`mb-0 hour-item ${itemValue === value ? 'hour-item-selected' : ''}`}
                  onClick={() => {
                    setValue(itemValue);
                    if (index === 0) {
                      setCustomText({ pro: 'included', mogul: 'included', custom: 'unlimited' });
                    } else {
                      setCustomText({
                        pro: `£${index * 19} / month`,
                        mogul: `£${index * 19} / month`,
                        custom: `unlimited`,
                      });
                    }
                    setIsOpen(false);
                  }}
                >
                  {`${thousandCommas(itemValue)}`}
                </p>
              );
            })}
        </div>
      )}
    </span>
  );
};

export default PricingPageMatrix;
